import { inject, Injectable, OnInit } from "@angular/core";
import { BehaviorSubject, map } from "rxjs";

import { AssignmentScheduleListModel } from "../models/assignment-schedule-list.model";
import { AssignmentScheduleService } from "./assignment-schedule.service";
import { AssignmentScheduleDetailsModel } from "../models/assignment-schedule-details.model";
import { AssignmentScheduleOptionModel } from "../models/assignment-schedule-option.model";
import { AssignmentSchedulesFilterModel } from "../models/assignment-schedules-filter.model";
import { TranslocoService } from "@jsverse/transloco";

@Injectable({
    providedIn: 'root'
})

export class AssignmentScheduleStateService implements OnInit {

    private assignmentScheduleService = inject(AssignmentScheduleService)
    private translocoService = inject(TranslocoService);

    public openAssignmentSchedulesSubject = new BehaviorSubject<AssignmentScheduleListModel[] | null>(null);
    public unansweredAssignmentSchedulesSubject = new BehaviorSubject<AssignmentScheduleListModel[] | null>(null);

    public assignmentScheduleDetailsSubject = new BehaviorSubject<AssignmentScheduleDetailsModel | null>(null);

    public assignmentSchedulesFilterSubject = new BehaviorSubject<AssignmentSchedulesFilterModel | null>(null);

    public isAnyFilterDisplayed = false;
    public isAnyFilterDisplayedEvent = new BehaviorSubject<boolean>(false);
    public isFullyDisabled = new BehaviorSubject<boolean>(false);

    public facilityItems: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
    public levelItems: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
    public weekdaysItems: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
    public myAnswersItems: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);


    public ngOnInit(): void {

        this.assignmentScheduleDetailsSubject.subscribe((model) => {
            console.log("Updating");
            if (model == null) return;

            const filter = this.assignmentSchedulesFilterSubject.value;
            if (filter == null) return;

            if (model.id == filter.assignmentScheduleId) return;

            this.resetAllItems();
        });
    }

    public loadOpenAssignmentSchedules(): void {
        this.unansweredAssignmentSchedulesSubject.next(null);

        this.assignmentScheduleService.getAssignmentSchedules().pipe(
            map(assignmentSchedules => this.sortAssignmentsChronologically(assignmentSchedules))
        ).subscribe({
            next: assignmentSchedules => {
                this.unansweredAssignmentSchedulesSubject.next(assignmentSchedules);
            }
        });
    }

    public loadUnansweredAssignmentSchedules(): void {
        this.unansweredAssignmentSchedulesSubject.next(null);

        this.assignmentScheduleService.getUnansweredAssignmentSchedules().pipe(
            map(assignmentSchedules => this.sortAssignmentsChronologically(assignmentSchedules))
        ).subscribe({
            next: assignmentSchedules => {
                this.unansweredAssignmentSchedulesSubject.next(assignmentSchedules);
            }
        });
    }

    public loadAssignmentScheduleAnswers(id: string): void {
        this.assignmentScheduleDetailsSubject.next(null);

        this.assignmentScheduleService.getAssignmentScheduleDetails(id)
            .subscribe({
                next: assignmentSchedules => {
                    assignmentSchedules.options = this.sortAssignmentScheduleOptionsChronologically(assignmentSchedules.options);
                    this.assignmentScheduleDetailsSubject.next(assignmentSchedules);
                    this.isFullyDisabled.next(false);
                }
            });
    }

    public disableAll(): void {
        this.isFullyDisabled.next(true);
    }

    public resetAllItems(): void {
        this.assignmentSchedulesFilterSubject.next(null)
    }

    public deleteLevelFilter(): void {
        const filter = this.assignmentSchedulesFilterSubject.value;
        if (filter == null) return;

        filter.levelIds = null;
        filter.levelNames = null;
        this.assignmentSchedulesFilterSubject.next(filter)
    }

    public deleteFacilityFilter(): void {
        const filter = this.assignmentSchedulesFilterSubject.value;
        if (filter == null) return;

        filter.facilityIds = null;
        filter.facilityNames = null;
        this.assignmentSchedulesFilterSubject.next(filter)
    }

    public deleteMyAnswerFilter(): void {
        const filter = this.assignmentSchedulesFilterSubject.value;
        if (filter == null) return;

        filter.myAnswerIds = null;
        filter.myAnswerNames = null;
        this.assignmentSchedulesFilterSubject.next(filter)
    }

    public deleteWeekdaysFilter(): void {
        const filter = this.assignmentSchedulesFilterSubject.value;
        if (filter == null) return;

        filter.isWeekdayFilterActive = false;
        filter.weekdays.isMondayActive = true;
        filter.weekdays.isTuesdayActive = true;
        filter.weekdays.isWednesdayActive = true;
        filter.weekdays.isThursdayActive = true;
        filter.weekdays.isFridayActive = true;
        filter.weekdays.isSaturdayActive = true;
        filter.weekdays.isSundayActive = true;

        this.assignmentSchedulesFilterSubject.next(filter)
    }


    public updateFilterDisplay(filterModel: AssignmentSchedulesFilterModel | null): void {
        if (filterModel == null) {
            this.facilityItems.next(null);
            this.levelItems.next(null);
            this.weekdaysItems.next(null);
            this.myAnswersItems.next(null);

            return;
        }

        this.facilityItems.next(filterModel.facilityNames?.sort((a, b) => a.localeCompare(b)) ?? null);
        this.levelItems.next(filterModel.levelNames?.sort((a, b) => a.localeCompare(b)) ?? null);
        this.myAnswersItems.next(filterModel.myAnswerNames?.sort((a, b) => a.localeCompare(b)) ?? null);
        this.weekdaysItems.next(this.getWeekdayItems(filterModel));
    }

    public updateIsAnyFilterDisplayed(): void {
        this.isAnyFilterDisplayed = this.getIsAnyFilterDisplayed();
        this.isAnyFilterDisplayedEvent.next(this.isAnyFilterDisplayed);
    }

    public getIsAnyFilterDisplayedEvent(): BehaviorSubject<boolean> {
        this.isAnyFilterDisplayedEvent = new BehaviorSubject<boolean>(this.isAnyFilterDisplayed);
        return this.isAnyFilterDisplayedEvent;
    }

    private sortAssignmentsChronologically(assignmentSchedules: AssignmentScheduleListModel[]): AssignmentScheduleListModel[] {
        return assignmentSchedules.sort((a, b) => {
            const dateA = new Date(a.from!).getTime();
            const dateB = new Date(b.from!).getTime();

            // Sort start time first
            const startDateComparison = dateA - dateB;
            if (startDateComparison !== 0) {
                return startDateComparison;
            }

            // If start dates equal sort by end time
            const endA = new Date(a.to!).getTime();
            const endB = new Date(b.to!).getTime();
            return endA - endB;
        });
    }

    private sortAssignmentScheduleOptionsChronologically(assignmentScheduleOption: AssignmentScheduleOptionModel[]): AssignmentScheduleOptionModel[] {
        return assignmentScheduleOption.sort((a, b) => {
            const dateA = new Date(a.from!).getTime();
            const dateB = new Date(b.from!).getTime();

            // Sort start time first
            const startDateComparison = dateA - dateB;
            if (startDateComparison !== 0) {
                return startDateComparison;
            }

            // If start dates equal sort by end time
            const endA = new Date(a.to!).getTime();
            const endB = new Date(b.to!).getTime();
            return endA - endB;
        });
    }

    private getWeekdayItems(filterModel: AssignmentSchedulesFilterModel): string[] {

        if (!filterModel.isWeekdayFilterActive) return [];
        if (this.getIsFullWeekActive(filterModel)) return [this.translocoService.translate('shared.weekdays.fullWeek')]

        let weekdayItems: string[] = [];

        if (filterModel.weekdays.isMondayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.1'));
        if (filterModel.weekdays.isTuesdayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.2'));
        if (filterModel.weekdays.isWednesdayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.3'));
        if (filterModel.weekdays.isThursdayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.4'));
        if (filterModel.weekdays.isFridayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.5'));
        if (filterModel.weekdays.isSaturdayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.6'));
        if (filterModel.weekdays.isSundayActive) weekdayItems.push(this.translocoService.translate('shared.weekdays.long.7'));

        return weekdayItems;
    }

    private getIsFullWeekActive(filterModel: AssignmentSchedulesFilterModel): boolean {
        if (!filterModel.weekdays.isMondayActive) return false;
        if (!filterModel.weekdays.isTuesdayActive) return false;
        if (!filterModel.weekdays.isWednesdayActive) return false;
        if (!filterModel.weekdays.isThursdayActive) return false;
        if (!filterModel.weekdays.isFridayActive) return false;
        if (!filterModel.weekdays.isSaturdayActive) return false;
        if (!filterModel.weekdays.isSundayActive) return false;

        return true;
    }

    private getIsAnyFilterDisplayed(): boolean {
        if (this.levelItems.value?.length) return true;
        if (this.facilityItems.value?.length) return true;
        if (this.weekdaysItems.value?.length) return true;
        if (this.myAnswersItems.value?.length) return true;

        return false;
    }
}

